import React, { useEffect, useState, useCallback } from "react";
import { api, formatBRL } from "../../lib/api";
import { maskAgenciaBR, maskContaBR, finalizeBankIdentBR } from "../../lib/numberFormat";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { Save, ShieldCheck, Building2, Bell, Landmark, Clock, X, Moon } from "lucide-react";
import { BankSelect } from "../../components/BankSelect";
import { bankByCode } from "../../lib/banks";
import { useTheme } from "../../lib/theme";
import CompanyDocuments, { ConfigTabs, PendenciaDocumentos } from "../../components/CompanyDocuments";

export default function CedenteSettings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "cedente_admin";
  const [theme, setTheme] = useTheme();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Liquidação mode: "pix_cnpj" or "conta_bancaria"
  const [liquidMode, setLiquidMode] = useState("pix_cnpj");

  // Bank account change request state (from API)
  const [bankChangeOpen, setBankChangeOpen] = useState(false);
  const [bankRequest, setBankRequest] = useState(null);
  const [bankForm, setBankForm] = useState({ banco: "", banco_nome: "", agencia: "", conta: "", tipo: "corrente" });
  // docs-cadastro (DOC13): aba Documentos dentro de Configurações do Cedente.
  const [aba, setAba] = useState("geral");

  const loadBankPending = useCallback(async () => {
    try {
      const { data } = await api.get("/cedente/bank-change/pending");
      setBankRequest(data);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.warn("Bank pending load error:", e);
      setBankRequest(null);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/cedente/settings");
      setData(data);
      setForm({
        contato_email: data.contato_email || "",
        contato_telefone: data.contato_telefone || "",
        pix_key: data.pix_key || "",
        notify_on_new_operation: data.notify_on_new_operation,
        notify_on_status_change: data.notify_on_status_change,
      });
      if (data.pix_key && data.pix_key === data.cnpj) {
        setLiquidMode("pix_cnpj");
      } else {
        setLiquidMode("conta_bancaria");
      }
    } catch (e) {
      toast.error("Erro ao carregar configurações");
    }
  }, []);

  useEffect(() => { load(); loadBankPending(); }, [load, loadBankPending]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        contato_email: form.contato_email || null,
        contato_telefone: form.contato_telefone || null,
        pix_key: liquidMode === "pix_cnpj" ? data.cnpj : form.pix_key || null,
        notify_on_new_operation: form.notify_on_new_operation,
        notify_on_status_change: form.notify_on_status_change,
      };
      await api.put("/cedente/settings", payload);
      toast.success("Configurações atualizadas");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(d => d.msg || JSON.stringify(d)).join(", "));
      } else {
        toast.error(typeof detail === "string" ? detail : "Falha ao salvar");
      }
    } finally { setSaving(false); }
  };

  const submitBankChange = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/cedente/bank-change", {
        banco: bankForm.banco,
        banco_nome: bankForm.banco_nome,
        agencia: bankForm.agencia,
        conta: bankForm.conta,
        tipo_conta: bankForm.tipo,
      });
      setBankRequest(data);
      setBankChangeOpen(false);
      setBankForm({ banco: "", banco_nome: "", agencia: "", conta: "", tipo: "corrente" });
      toast.success("Solicitação de alteração enviada para aprovação");
    } catch (e) {
      // FS-247/B1: o 422 do FastAPI vem como ARRAY de erros por campo — o
      // `typeof detail === "string"` caía sempre no genérico "Erro ao
      // solicitar alteração" e o cedente não ficava sabendo QUAL campo
      // recusou. Mesmo tratamento do `save()` acima, que já fazia certo.
      const detail = e?.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(d => d.msg || JSON.stringify(d)).join(", "));
      } else {
        toast.error(typeof detail === "string" ? detail : "Erro ao solicitar alteração");
      }
    }
  };

  const cancelBankRequest = async () => {
    if (!bankRequest?.id) return;
    try {
      await api.delete(`/cedente/bank-change/${bankRequest.id}`);
      setBankRequest(null);
      toast.info("Solicitação de alteração cancelada");
    } catch (e) {
      toast.error("Erro ao cancelar solicitação");
    }
  };

  if (!data) return <div className="text-zinc-500 p-8">Carregando…</div>;

  // FS-247: este bloco era um MOCK hardcoded ("Banco Zemo"/"0001"/"12345-6") —
  // dado FALSO sobre o DESTINO DO DINHEIRO, exibido a todo cedente (inclusive
  // ao lado da "Conta Solicitada" de um pedido pendente, onde a comparação
  // entre a conta atual e a nova é justamente o ponto). Passa a sair do
  // cadastro real; onde o cadastro não tem valor, exibe "—" — nunca inventa.
  //
  // FS-247-B: GET /cedente/settings já devolve banco/agencia/conta/tipo_conta
  // do cadastro real, sem máscara (decisão do owner).
  const TRACO = "—";
  const bancoAtual = bankByCode(data.banco);
  const currentBank = {
    banco: bancoAtual?.label || data.banco || TRACO,
    agencia: data.agencia || TRACO,
    conta: data.conta || TRACO,
    tipo: data.tipo_conta === "poupanca"
      ? "Poupança"
      : data.tipo_conta === "corrente" ? "Corrente" : TRACO,
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="cedente-settings-page">
      <div>
        <span className="zemo-overline">Conta</span>
        <h1 className="mt-1.5 text-3xl sm:text-[34px] font-semibold tracking-tight">Configurações</h1>
        <p className="mt-1 text-sm text-zinc-500">Dados cadastrais, bancários e preferências de notificação.</p>
      </div>

      <ConfigTabs aba={aba} onChange={setAba} />
      {aba === "documentos" && (
        <CompanyDocuments doc={data.cnpj} canUpload={["cedente_admin", "cedente_operator"].includes(user?.role)} />
      )}
      {aba !== "documentos" && (<>
      {/* Owner C1009756/C1009769: o mesmo aviso de pendência também na aba inicial. */}
      <PendenciaDocumentos doc={data.cnpj} />
      {/* Identity */}
      <div className="zemo-card p-6">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-md bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-700">
            <Building2 className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold tracking-tight">{data.razao_social}</h3>
            <p className="text-[13px] text-zinc-500 mt-0.5 font-mono-num">{data.cnpj}</p>
          </div>
        </div>
        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          {[
            ["Nome Fantasia", data.nome_fantasia],
            ["Cidade/UF", `${data.cidade} · ${data.uf}`],
            ["Limite Aprovado", formatBRL(data.limite_aprovado)],
            ["Score", String(data.score)],
          ].map(([k, v]) => (
            <div key={k} className="p-3 border border-[var(--zemo-border)] rounded-md bg-zinc-50/30">
              <div className="zemo-overline">{k}</div>
              <div className="mt-1 font-semibold text-zinc-900 text-sm">{v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Bank / Liquidation */}
      <div className="zemo-card p-6 space-y-5" data-testid="ced-bank-section">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-700">
            <Landmark className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold tracking-tight">Dados para Liquidação</h3>
            <p className="text-[12px] text-zinc-500">Como você recebe os valores das operações.</p>
          </div>
        </div>

        {/* Mode toggle */}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => isAdmin && setLiquidMode("pix_cnpj")}
            className={`p-3 rounded-md border text-left transition-all ${liquidMode === "pix_cnpj" ? "border-[#0055FF] bg-blue-50/50 ring-1 ring-[#0055FF]" : "border-[var(--zemo-border)] hover:border-zinc-300"}`}
            data-testid="liquid-mode-pix"
          >
            <div className="text-sm font-semibold text-zinc-900">Chave PIX em meu CNPJ</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">Receber via PIX usando o CNPJ cadastrado</div>
          </button>
          <button
            type="button"
            onClick={() => isAdmin && setLiquidMode("conta_bancaria")}
            className={`p-3 rounded-md border text-left transition-all ${liquidMode === "conta_bancaria" ? "border-[#0055FF] bg-blue-50/50 ring-1 ring-[#0055FF]" : "border-[var(--zemo-border)] hover:border-zinc-300"}`}
            data-testid="liquid-mode-bank"
          >
            <div className="text-sm font-semibold text-zinc-900">Conta Bancária</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">Receber via TED/transferência bancária</div>
          </button>
        </div>

        {/* PIX CNPJ mode */}
        {liquidMode === "pix_cnpj" && (
          <div className="p-3 border border-emerald-200 rounded-md bg-emerald-50/50">
            <div className="text-sm text-emerald-900">
              Liquidações serão feitas via PIX para o CNPJ: <span className="font-semibold font-mono-num">{data.cnpj}</span>
            </div>
          </div>
        )}

        {/* Bank account mode */}
        {liquidMode === "conta_bancaria" && (
          <>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                ["Banco", currentBank.banco, "ced-current-bank-banco"],
                ["Agência", currentBank.agencia, "ced-current-bank-agencia"],
                ["Conta", currentBank.conta, "ced-current-bank-conta"],
                ["Tipo", currentBank.tipo, "ced-current-bank-tipo"],
              ].map(([k, v, tid]) => (
                <div key={k} className="p-3 border border-[var(--zemo-border)] rounded-md bg-zinc-50/30">
                  <div className="zemo-overline">{k}</div>
                  <div className="mt-1 font-semibold text-zinc-900 text-sm font-mono-num" data-testid={tid}>{v}</div>
                </div>
              ))}
            </div>

            {/* Pending Change Request */}
            {bankRequest && (
              <div className="border border-amber-200 rounded-md bg-amber-50 p-4 space-y-3" data-testid="bank-change-pending">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-amber-600" />
                  <span className="text-sm font-semibold text-amber-900">Alteração pendente de aprovação</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <div className="zemo-overline text-amber-700">Conta Atual</div>
                    <div className="mt-1 text-sm text-zinc-700">{currentBank.banco} · Ag {currentBank.agencia} · CC {currentBank.conta}</div>
                  </div>
                  <div>
                    <div className="zemo-overline text-amber-700">Conta Solicitada</div>
                    <div className="mt-1 text-sm text-zinc-900 font-semibold">{bankRequest.banco_nome || bankRequest.banco} · Ag {bankRequest.agencia} · {bankRequest.tipo_conta === "corrente" ? "CC" : "CP"} {bankRequest.conta}</div>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={cancelBankRequest} data-testid="bank-change-cancel">
                  <X className="h-3.5 w-3.5 mr-1" /> Cancelar solicitação
                </Button>
              </div>
            )}

            {/* Request Change Button */}
            {!bankRequest && isAdmin && !bankChangeOpen && (
              <Button variant="outline" onClick={() => setBankChangeOpen(true)} data-testid="bank-change-request-btn">
                Solicitar alteração de conta
              </Button>
            )}

            {/* Change Form */}
            {bankChangeOpen && (
              <form onSubmit={submitBankChange} className="border border-[var(--zemo-border)] rounded-md p-4 space-y-4 bg-white" data-testid="bank-change-form">
                <h4 className="text-sm font-semibold">Nova conta para liquidação</h4>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Banco</Label>
                    {/* FS-247: `banco` = código COMPE-3 (coluna String(10)) e
                        `banco_nome` = nome do banco — os dois campos que o BO
                        espera separados (schema_backoffice_v31.sql: banco
                        VARCHAR(10) / banco_nome VARCHAR(255)). Antes ia o
                        rótulo inteiro em `banco` e `banco_nome` vazio. */}
                    <BankSelect
                      value={bankForm.banco}
                      onChange={(code) => setBankForm({
                        ...bankForm,
                        banco: code,
                        banco_nome: bankByCode(code)?.name || "",
                      })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Tipo de conta</Label>
                    <select className="w-full h-10 rounded-md border border-input px-3 text-sm" value={bankForm.tipo} onChange={(e) => setBankForm({ ...bankForm, tipo: e.target.value })}>
                      <option value="corrente">Conta Corrente</option>
                      <option value="poupanca">Conta Poupança</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Agência</Label>
                    <Input required inputMode="text" value={bankForm.agencia} onChange={(e) => setBankForm({ ...bankForm, agencia: maskAgenciaBR(e.target.value, bankForm.agencia) })} onBlur={(e) => setBankForm({ ...bankForm, agencia: finalizeBankIdentBR(e.target.value) })} placeholder="0001-2" className="font-mono-num" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Conta</Label>
                    <Input required inputMode="text" value={bankForm.conta} onChange={(e) => setBankForm({ ...bankForm, conta: maskContaBR(e.target.value, bankForm.conta) })} onBlur={(e) => setBankForm({ ...bankForm, conta: finalizeBankIdentBR(e.target.value) })} placeholder="12345-6" className="font-mono-num" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="ghost" onClick={() => setBankChangeOpen(false)}>Cancelar</Button>
                  <Button type="submit" className="bg-[#0055FF] hover:bg-[#0044CC] text-white" disabled={!bankForm.banco}>Enviar para aprovação</Button>
                </div>
                <p className="text-[11px] text-zinc-400">A alteração será analisada pelo backoffice antes de ser efetivada.</p>
              </form>
            )}
          </>
        )}
      </div>

      {/* Appearance */}
      <div className="zemo-card p-6" data-testid="ced-appearance-section">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-zinc-100 border border-[var(--zemo-border)] flex items-center justify-center text-zinc-700">
              <Moon className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-base font-semibold tracking-tight">Aparência</h3>
              <p className="text-[12px] text-zinc-500">Tema escuro reduz o brilho da interface. Preferência salva neste dispositivo.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[13px] text-zinc-500">Tema escuro</span>
            <Switch
              checked={theme === "dark"}
              onCheckedChange={(v) => setTheme(v ? "dark" : "light")}
              data-testid="ced-theme-switch"
            />
          </div>
        </div>
      </div>

      {/* Contacts & Notifications */}
      <form onSubmit={save} className="zemo-card p-6 space-y-5">
        <div>
          <span className="zemo-overline">Contato</span>
          <h3 className="mt-1 text-lg font-semibold tracking-tight">Dados de comunicação</h3>
          <p className="text-[13px] text-zinc-500 mt-0.5">Usados pelo seu parceiro para envio de avisos e SLAs operacionais.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>E-mail</Label>
            <Input type="email" value={form.contato_email} onChange={(e) => setForm({ ...form, contato_email: e.target.value })} disabled={!isAdmin} placeholder="contato@empresa.com.br" data-testid="ced-settings-email" />
          </div>
          <div className="space-y-1.5">
            <Label>Telefone</Label>
            <Input value={form.contato_telefone} onChange={(e) => setForm({ ...form, contato_telefone: e.target.value })} disabled={!isAdmin} placeholder="+55 11 99999-9999" data-testid="ced-settings-phone" />
          </div>
        </div>

        <div className="border-t border-[var(--zemo-border)] pt-5">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-zinc-50 border border-[var(--zemo-border)] flex items-center justify-center text-zinc-700">
              <Bell className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold tracking-tight">Notificações</h3>
              <p className="text-[12px] text-zinc-500">Avisos por e-mail sobre o ciclo das operações.</p>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <SettingsToggle label="Nova operação criada" description="Quando seu parceiro criar uma operação em seu nome." checked={!!form.notify_on_new_operation} onChange={(v) => setForm({ ...form, notify_on_new_operation: v })} disabled={!isAdmin} testid="ced-settings-notify-new" />
            <SettingsToggle label="Mudança de status" description="Quando uma operação mudar de status (aprovada, liquidada, etc.)." checked={!!form.notify_on_status_change} onChange={(v) => setForm({ ...form, notify_on_status_change: v })} disabled={!isAdmin} testid="ced-settings-notify-status" />
          </div>
        </div>

        {isAdmin && (
          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white h-10 font-semibold" data-testid="ced-settings-save">
              <Save className="h-4 w-4 mr-1.5" /> {saving ? "Salvando…" : "Salvar alterações"}
            </Button>
          </div>
        )}
        {!isAdmin && (
          <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-[12px] p-3 flex items-start gap-2">
            <ShieldCheck className="h-4 w-4 mt-0.5" /> Apenas o Admin Cedente pode alterar estas configurações.
          </div>
        )}
      </form>
      </>)}
    </div>
  );
}

function SettingsToggle({ label, description, checked, onChange, disabled, testid }) {
  return (
    <div className="flex items-center justify-between rounded-md border border-[var(--zemo-border)] p-3">
      <div className="pr-3">
        <div className="text-[13px] font-medium">{label}</div>
        <div className="text-[11px] text-zinc-500">{description}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testid} />
    </div>
  );
}
